import { useCallback, useEffect, useState } from 'react';

/**
 * Configuração de proporção tabela/detalhe nos layouts split (vertical/horizontal).
 *
 *   DEFAULT_SPLIT_RATIO = 50  → tabela 50% / detail 50%
 *   Para mudar permanentemente o padrão, edite essa constante e remova o localStorage
 *   `op:split:*` no DevTools (ou troca via drag do splitter na UI, que persiste).
 *
 * O ratio é por modo (`vertical` e `horizontal` salvam separado), permitindo que o
 * usuário use proporções diferentes em cada layout.
 */
export const DEFAULT_SPLIT_RATIO = 65;
// Min-width do PAINEL DE DETALHE (DetailPanel) em qualquer layout split/drawer.
// 360px = mínimo absoluto onde o painel ainda é usável (tabs com scroll-x,
// header com ações em coluna). Em viewport ~958px (browser em 50% de monitor
// 1920) + ratio 65 → tabela ~622, detail ~336 → detail entra com 360 forçado
// e tabela encolhe pra ~598. Antes era 460 e empurrava o detail PRA FORA da
// viewport quando container era apertado. DetailPanel interno (tabs/body)
// faz overflow horizontal próprio pra absorver o aperto.
export const MIN_DETAIL_PX = 360;
const STORAGE_KEY_PREFIX = 'op:split:';
const MIN_RATIO = 20;
const MAX_RATIO = 80;

function readStored(layoutMode) {
    try {
        const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${layoutMode}`);
        if (!raw) {
            return null;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? ClampRatio(n) : null;
    } catch {
        return null;
    }
}

function persistStored(layoutMode, value) {
    try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${layoutMode}`, String(value));
    } catch {
        // ignore localStorage errors (private mode, quota)
    }
}

export function ClampRatio(value) {
    return Math.min(MAX_RATIO, Math.max(MIN_RATIO, Number(value) || DEFAULT_SPLIT_RATIO));
}

/**
 * Hook: ratio percentual da tabela em layouts split.
 *
 *   const { ratio, setRatio } = useSplitRatio('vertical')
 *   // ratio=50 → tabela 50%, detail 50%
 *
 * setRatio recebe percentual da TABELA. Detail vira automaticamente (100 - ratio).
 */
export function useSplitRatio(layoutMode) {
    const [ratio, setRatioState] = useState(() => readStored(layoutMode) ?? DEFAULT_SPLIT_RATIO);

    // Quando o modo muda, sincroniza estado com localStorage daquele modo.
    useEffect(() => {
        setRatioState(readStored(layoutMode) ?? DEFAULT_SPLIT_RATIO);
    }, [layoutMode]);

    const setRatio = useCallback(
        (next) => {
            const safe = ClampRatio(next);
            setRatioState(safe);
            persistStored(layoutMode, safe);
        },
        [layoutMode],
    );

    return { ratio, setRatio };
}
